import { UserSettingService, type UserSetting } from "@sal/onevent-portal";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SystemName } from "../constants";

export type DashboardViewSetting = {
	systemName: string;
	on: string;
	off: string;
	defaultEnabled: boolean;
};

export function isDashboardViewEnabled(setting: DashboardViewSetting, settingValue: unknown) {
	return settingValue === setting.on ? true : settingValue === setting.off ? false : setting.defaultEnabled;
}

export const DashboardViewSettings = {
	mapView: {
		systemName: SystemName.UserSetting.DashboardMapView,
		on: "map",
		off: "default",
		defaultEnabled: false
	},
	blockFilters: {
		systemName: SystemName.UserSetting.DashboardBlockFilters,
		on: "show",
		off: "hidden",
		defaultEnabled: true
	}
} as const;

// Read via UserSetting/List: the single UserSetting GET is served from a server-side cache that
// UserSetting/UpdateOther does not invalidate, so admin changes never reach the user.
const queryKey = ["/UserSetting/List", SystemName.SettingType.PortalUISetting];

export function useDashboardViewSetting(setting: DashboardViewSetting) {
	const userSettingService = new UserSettingService();
	const queryClient = useQueryClient();

	const { data, isLoading } = useQuery(
		queryKey,
		() => userSettingService.getListSetting({ settingTypeSystemName: SystemName.SettingType.PortalUISetting }),
		{ cacheTime: 0 }
	);

	const userSettingMutation = useMutation({
		mutationFn: (next: boolean) => {
			return userSettingService.saveSetting({
				systemName: setting.systemName,
				settingTypeSystemName: SystemName.SettingType.PortalUISetting,
				setting: next ? setting.on : setting.off
			});
		},
		onSuccess: () => {
			return queryClient.invalidateQueries(queryKey);
		}
	});

	const settingValue = (data?.data as Array<UserSetting> | undefined)?.find(
		(x) => x.systemName === setting.systemName
	)?.setting;

	return [
		isDashboardViewEnabled(setting, settingValue),
		(next: boolean) => userSettingMutation.mutateAsync(next),
		isLoading
	] as const;
}
